// Reusable full-screen launch loader — the progress bar shown while a MAP is
// being built (geometry merge + FX pools + shader precompile in MatchMode).
//
// This is DELIBERATELY separate from the boot overlay in main.ts: boot warms
// global textures once at page load; THIS one covers the per-map launch, which
// used to be a silent multi-second freeze with only a tiny HUD status line.
//
// It is a singleton mounted on <body> (not #ui), so it survives showScreen()'s
// replaceChildren on #ui and sits above the HUD (z-index below the boot loader,
// above everything else). Every entry point drives the same instance:
//   showLoading()  — reveal + reset to 0 (call on the launch click for instant
//                    feedback, before the room even connects)
//   setLoading()   — update the bar + label as the build progresses
//   hideLoading()  — fade out + detach (first rendered frame, or on any error)

interface LoaderDom {
  root: HTMLElement;
  copy: HTMLElement;
  fill: HTMLElement;
  meta: HTMLElement;
  sub: HTMLElement;
}

let dom: LoaderDom | null = null;
let visible = false;
let removeTimer = 0;

function build(): LoaderDom {
  const root = document.createElement("div");
  root.className = "match-loader";
  root.setAttribute("role", "progressbar");
  root.innerHTML = `
    <div class="ml-inner">
      <div class="ml-brand">NEON PURGE</div>
      <div class="ml-sub">ENTERING THE ARENA</div>
      <div class="ml-copy">CONNECTING</div>
      <div class="ml-track"><div class="ml-fill"></div><div class="ml-scan"></div></div>
      <div class="ml-meta">0%</div>
    </div>`;
  document.body.appendChild(root);
  return {
    root,
    sub: root.querySelector<HTMLElement>(".ml-sub")!,
    copy: root.querySelector<HTMLElement>(".ml-copy")!,
    fill: root.querySelector<HTMLElement>(".ml-fill")!,
    meta: root.querySelector<HTMLElement>(".ml-meta")!,
  };
}

/** Reveal the loader and reset it. `sub` is a small line under the brand — pass
 *  the map name once it is known ("NEO ISLAND") for a nicer launch screen. */
export function showLoading(label = "CONNECTING", sub?: string): void {
  window.clearTimeout(removeTimer);
  if (!dom) dom = build();
  dom.root.classList.remove("done");
  if (sub !== undefined) dom.sub.textContent = sub;
  dom.copy.textContent = label;
  dom.fill.style.width = "0%";
  dom.meta.textContent = "0%";
  // force a reflow so a re-show after a fade-out animates from 0 again
  void dom.root.offsetWidth;
  visible = true;
}

/** Update progress (0..1) and the phase label. No-op if hidden — so a late
 *  callback after hideLoading() never resurrects the overlay. */
export function setLoading(progress: number, label?: string, sub?: string): void {
  if (!dom || !visible) return;
  const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
  if (label !== undefined) dom.copy.textContent = label;
  if (sub !== undefined) dom.sub.textContent = sub;
  dom.fill.style.width = `${pct}%`;
  dom.meta.textContent = `${pct}%`;
}

/** Fade out and detach. Idempotent — safe to call from every teardown path. */
export function hideLoading(): void {
  visible = false;
  if (!dom) return;
  const el = dom.root;
  dom = null;
  el.classList.add("done");
  removeTimer = window.setTimeout(() => el.remove(), 320);
}

/** Yield to the browser so the loader can actually PAINT the latest progress
 *  before the next blocking build phase runs. A double rAF resumes AFTER a
 *  full paint (single rAF fires before paint). This is what turns one giant
 *  synchronous stall into bracketed, visibly-progressing chunks.
 *
 *  rAF is paused/throttled while the tab is backgrounded, which would stall the
 *  whole load until the player returns — so we race it against a short timeout
 *  fallback. When visible, the rAF wins and we get a real paint; when hidden,
 *  the timeout keeps the build moving (a paint is moot anyway). */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => { if (!done) { done = true; resolve(); } };
    requestAnimationFrame(() => requestAnimationFrame(finish));
    setTimeout(finish, 120);
  });
}
